import React, { CSSProperties, useState } from "react";

import { Divider, Grid, Header, Image, Modal } from "semantic-ui-react";

export type MenuHasVideoType = {
  sequence: number;
  id: number;
  active: boolean;
  created_at: string;
  menu: string;
  title: string;
  video_link: string;
};

type ModalVideoTutorialProps = {
  open: boolean;
  videos: MenuHasVideoType[];
  onClose: () => void;
};

type Styles = Record<
  "relatedVideoCard" | "thumbnail" | "videoIframe" | "videoTitle" | "videoWrapper",
  CSSProperties
>;

const styles: Styles = {
  relatedVideoCard: {
    backgroundColor: "#fafafa",
    borderRadius: "8px",
    cursor: "pointer",
    marginBottom: "10px",
    padding: "6px",
    transition: "all 0.3s ease",
  },
  thumbnail: {
    aspectRatio: "16/9",
    borderRadius: "6px",
    objectFit: "cover",
    width: "100%",
  },
  videoIframe: {
    backgroundColor: "white",
    border: "none",
    borderRadius: "8px",
    height: "100%",
    left: 0,
    position: "absolute",
    top: 0,
    width: "100%",
  },
  videoTitle: {
    color: "#333",
    display: "-webkit-box",
    fontSize: "12px",
    fontWeight: "500",
    lineHeight: "1.4",
    overflow: "hidden",
    paddingLeft: "4px",
    paddingRight: "4px",
    paddingTop: "6px",
    textOverflow: "ellipsis",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
  },
  videoWrapper: {
    borderRadius: "8px",
    boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
    height: 0,
    overflow: "hidden",
    paddingBottom: "56.25%",
    position: "relative",
  },
};

// ฟังก์ชันดึง Video ID จาก YouTube URL
const getYouTubeVideoId = (url: string): string => {
  if (url.includes("youtu.be/")) {
    return url.split("youtu.be/")[1].split("?")[0];
  }

  if (url.includes("youtube.com/watch?v=")) {
    return url.split("v=")[1].split("&")[0];
  }

  return "";
};

// ฟังก์ชันแปลง YouTube URL ให้เป็น embed format
const getYouTubeEmbedUrl = (url: string): string => {
  if (url.includes("youtube.com/embed/")) {
    return url;
  }
  const videoId = getYouTubeVideoId(url);

  return `https://www.youtube.com/embed/${videoId}`;
};

// ฟังก์ชันดึง thumbnail จาก YouTube URL
const getYouTubeThumbnail = (url: string): string => {
  const videoId = getYouTubeVideoId(url);

  return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
};

const ModalVideoTutorial: React.FC<ModalVideoTutorialProps> = ({ open, videos = [], onClose }) => {
  const sortedVideos = [...videos].sort((a, b) => a.sequence - b.sequence);

  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (sortedVideos.length === 0) {
    return null;
  }

  const currentVideo = sortedVideos[currentVideoIndex];
  const embedUrl = getYouTubeEmbedUrl(currentVideo.video_link);
  const showRelatedVideos = sortedVideos.length > 1;

  const handleVideoClick = (index: number) => {
    setCurrentVideoIndex(index);
  };

  return (
    <Modal
      style={{ borderRadius: "15px", maxWidth: showRelatedVideos ? "1000px" : "900px" }}
      closeOnDimmerClick={false}
      open={open}
      size="large"
      closeIcon
      onClose={onClose}
    >
      <Modal.Header style={{ border: "none", padding: "1.25rem 2.25rem" }}>
        <div style={{ color: "#333", fontSize: "18px", fontWeight: "bold" }}>
          วิดีโอสอนการใช้งาน
        </div>
        <Divider style={{ marginBottom: 0 }} />
      </Modal.Header>

      <Modal.Content style={{ padding: "0 2.25rem 32px 2.25rem" }}>
        <Header
          style={{ color: "#333", fontSize: "16px", fontWeight: "normal", marginBottom: "16px" }}
          as="h3"
        >
          {currentVideo.title}
        </Header>

        <Grid style={{ margin: 0 }} stackable>
          <Grid.Row style={{ padding: 0 }}>
            {/* Video Player - Left Side */}
            <Grid.Column
              style={{
                paddingBottom: 0,
                paddingLeft: 0,
                paddingRight: showRelatedVideos ? "20px" : "0",
                paddingTop: 0,
              }}
              width={showRelatedVideos ? 13 : 16}
            >
              <div style={styles.videoWrapper}>
                <iframe
                  style={styles.videoIframe}
                  src={embedUrl}
                  title="Video Tutorial"
                  allowFullScreen
                />
              </div>
            </Grid.Column>

            {/* Related Videos - Right Side */}
            {showRelatedVideos && (
              <Grid.Column style={{ padding: 0 }} width={3}>
                <div
                  style={{
                    maxHeight: "420px",
                    overflowX: "hidden",
                    overflowY: "auto",
                  }}
                >
                  {sortedVideos.map(
                    (video, index) =>
                      index !== currentVideoIndex && (
                        <div
                          key={video.id}
                          style={
                            hoveredIndex === index
                              ? {
                                  ...styles.relatedVideoCard,
                                  backgroundColor: "#ffffff",
                                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                                }
                              : styles.relatedVideoCard
                          }
                          onClick={() => handleVideoClick(index)}
                          onMouseEnter={() => setHoveredIndex(index)}
                          onMouseLeave={() => setHoveredIndex(null)}
                        >
                          <div
                            style={{
                              border: "1px solid #e8e8e8",
                              borderRadius: "6px",
                            }}
                          >
                            <Image
                              style={styles.thumbnail}
                              src={getYouTubeThumbnail(video.video_link)}
                            />
                          </div>
                          <div style={styles.videoTitle}>{video.title}</div>
                        </div>
                      )
                  )}
                </div>
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
      </Modal.Content>
    </Modal>
  );
};

export default ModalVideoTutorial;
